import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { TypingAnimator } from '../../../../services/ui/TypingAnimator';
import { configurationService } from '../../QnAConfigModal/services';
import { BackendQnAParams } from '../../QnAConfigModal/services/types';
import {
  QnAAIServiceManager,
  AIMessage,
  AIServiceError,
  StreamingAIResponse,
  AIServiceOptions,
} from '../services';

export interface UseAIServiceManagerReturn {
  aiServiceManager: QnAAIServiceManager | null;
  isAIBusy: boolean;
  refreshAIConfiguration: () => Promise<void>;
  sendMessageToAI: (
    messages: AIMessage[],
    context: string | undefined,
    onMessageUpdate: (messageId: string, content: string) => void,
    messageId: string
  ) => Promise<void>;
}

const createAIServiceManager = async (): Promise<QnAAIServiceManager> => {
  const config = await configurationService.getConfig();

  // Always use backend provider
  const effectiveProvider = 'backend' as const;

  // Build backend override parameters (only include if override flag is enabled)
  const overrideParams: BackendQnAParams = {};
  if (config.overrideProvider && config.provider) {
    overrideParams.provider = config.provider;
  }
  if (config.overrideModel && config.model) {
    overrideParams.model = config.model;
  }
  if (config.overrideMaxTokens && config.maxTokens !== undefined) {
    overrideParams.maxTokens = config.maxTokens;
  }
  if (config.overrideTemperature && config.temperature !== undefined) {
    overrideParams.temperature = config.temperature;
  }
  if (config.overrideDocumentId && config.documentId) {
    overrideParams.documentId = config.documentId;
  }

  const managerConfig: AIServiceOptions = {
    provider: effectiveProvider,
    config: {
      timeout: 120000, // Backend timeout
      overrideParams, // Backend override parameters
    },
  };

  return new QnAAIServiceManager(managerConfig);
};

export function useAIServiceManager(): UseAIServiceManagerReturn {
  const [aiServiceManager, setAiServiceManager] = useState<QnAAIServiceManager | null>(null);
  const [isAIBusy, setIsAIBusy] = useState(false);
  const animatorRef = useRef<TypingAnimator | null>(null);

  const initializeAIService = useCallback(async () => {
    try {
      const manager = await createAIServiceManager();
      setAiServiceManager(manager);
    } catch (error) {
      throw new Error('Failed to initialize AI Service');
    }
  }, []);

  useEffect(() => {
    initializeAIService();
  }, [initializeAIService]);

  useEffect(() => () => {
    animatorRef.current?.forceStop();
  }, []);

  const refreshAIConfiguration = useCallback(async () => {
    const manager = await createAIServiceManager();
    setAiServiceManager(manager);
  }, []);

  const sendMessageToAI = useCallback(async (
    messages: AIMessage[],
    context: string | undefined,
    onMessageUpdate: (messageId: string, content: string) => void,
    messageId: string,
  ) => {
    if (!aiServiceManager) {
      throw new AIServiceError('AI Service is not initialized yet. Please wait...', 'CONFIG_ERROR');
    }

    setIsAIBusy(true);

    try {
      animatorRef.current = new TypingAnimator(
        (text) => onMessageUpdate(messageId, text),
        () => {
          /* Animation complete */
        },
      );

      let previousContentLength = 0;

      await aiServiceManager.generateStreamingResponse(
        messages,
        context,
        (chunk: StreamingAIResponse) => {
          if (chunk.content.length > previousContentLength) {
            const newText = chunk.content.slice(previousContentLength);
            animatorRef.current?.add(newText);
          }
          previousContentLength = chunk.content.length;
        },
      );

      animatorRef.current?.finish();
    } finally {
      setIsAIBusy(false);
    }
  }, [aiServiceManager]);

  return {
    aiServiceManager,
    isAIBusy,
    refreshAIConfiguration,
    sendMessageToAI,
  };
}
